import axios, { AxiosHeaders } from 'axios';
import { isNil, template, path, mergeDeepRight } from '@worksolutions/utils';
import { p as plainToClass } from './classes_chunkExport-DIIf8JYD.mjs';

function _define_property$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
class AppRequestError extends Error {
    static isRequestError(data) {
        return data instanceof AppRequestError;
    }
    static prepareAxiosConfigForDebug(config) {
        return {
            timeout: config.timeout,
            headers: config.headers,
            url: config.url,
            baseURL: config.baseURL,
            method: config.method,
            data: config.data
        };
    }
    // Хост с несколькими адресами Node перебирает (autoSelectFamily) и при неудаче бросает AggregateError
    // с пустым message, а axios подменяет пустой message на "Error". Причины по каждому адресу — в cause.errors.
    // Поле проверяется без instanceof: kit попадает и во фронт с таргетом es2020, где AggregateError может не быть.
    static getMessage(error) {
        if (error.response?.data?.message) return error.response.data.message;
        const addressErrors = error.cause?.errors;
        if (!addressErrors?.length) return error.message;
        return addressErrors.map((addressError)=>addressError.message).join("; ");
    }
    static buildFromAxiosError(error, autoLogger) {
        const axiosConfig = this.prepareAxiosConfigForDebug(error.config);
        return new AppRequestError({
            message: this.getMessage(error),
            statusCode: error.response?.status ?? -1,
            response: error.response,
            requestConfig: axiosConfig,
            cancelled: axios.isCancel(error),
            messageGroup: `Axios error while ${axiosConfig.method.toUpperCase()} ${axiosConfig.url}`,
            autoLogger
        }, {
            stack: error.stack,
            originalError: error
        });
    }
    serialize() {
        return {
            message: this.data.message,
            group: this.data.messageGroup,
            statusCode: this.data.statusCode,
            responseData: this.data.response?.data ?? null,
            requestConfig: this.data.requestConfig
        };
    }
    constructor(data, debug){
        super(data.message), _define_property$1(this, "data", void 0), _define_property$1(this, "debug", void 0), this.data = data, this.debug = debug;
    }
}

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
var METHODS = /*#__PURE__*/ function(METHODS) {
    METHODS["POST"] = "post";
    METHODS["GET"] = "get";
    METHODS["PUT"] = "put";
    METHODS["PATCH"] = "patch";
    METHODS["DELETE"] = "delete";
    return METHODS;
}({});
class RequestManager {
    static observerStart(config) {
        for (const observer of RequestManager.observers){
            try {
                observer.onStart(config);
            } catch (e) {
                console.error(e);
            }
        }
    }
    static observerSuccess(config, axiosResponse) {
        for (const observer of RequestManager.observers){
            try {
                observer.onSuccess(config, axiosResponse);
            } catch (e) {
                console.error(e);
            }
        }
    }
    static observerError(config, error) {
        for (const observer of RequestManager.observers){
            try {
                observer.onError(config, error);
            } catch (e) {
                console.error(e);
            }
        }
    }
    getBaseUrl() {
        return this.baseURL;
    }
    async applyAllBeforeRequestMiddleware(config) {
        for(let i = 0; i < this.beforeRequestMiddleware.length; i++){
            const middleware = this.beforeRequestMiddleware[i];
            try {
                await middleware({
                    config
                });
            } catch (e) {
                console.error(e);
            }
        }
    }
    async applyAllAfterRequestMiddleware(config, axiosResponse) {
        for(let i = 0; i < this.afterRequestMiddleware.length; i++){
            const middleware = this.afterRequestMiddleware[i];
            try {
                await middleware({
                    config,
                    axiosResponse
                });
            } catch (e) {
                console.error(e);
            }
        }
    }
    async appleAllAfterRequestSuccessMiddleware(mutableConfig, axiosResponse) {
        for(let i = 0; i < this.afterRequestSuccessMiddleware.length; i++){
            const middleware = this.afterRequestSuccessMiddleware[i];
            try {
                await middleware({
                    mutableConfig,
                    axiosResponse
                });
            } catch (e) {
                console.error(e);
            }
        }
    }
    async applyAllErrorMiddleware(config, { requestData = {}, error, makeRequest }) {
        if (requestData.disableErrorMiddlewares) return error;
        const shareData = {};
        for(let i = 0; i < this.errorMiddleware.length; i++){
            const middleware = this.errorMiddleware[i];
            const middlewareResult = await middleware({
                error,
                config,
                shareData,
                makeRequest
            });
            if (AppRequestError.isRequestError(middlewareResult)) {
                error = middlewareResult;
                continue;
            }
            if (isNil(middlewareResult)) break;
            return middlewareResult;
        }
        return error;
    }
    async makeRequest({ url, method, responseType, headers, requestConfig, requestData: { uploadProgressReceiver, downloadProgressReceiver, urlParams, additionalQueryParams, body, headers: requestDataHeaders = {}, abortSignal } }) {
        const requestData = {
            url,
            method,
            adapter: this.requestManagerOptions.axiosAdapter,
            baseURL: this.baseURL,
            responseType,
            headers: new AxiosHeaders(Object.assign({
                Accept: "application/json"
            }, headers, requestDataHeaders)),
            signal: abortSignal
        };
        if (requestConfig.contentType) requestData.headers["Content-Type"] = requestConfig.contentType;
        requestData[method === "get" ? "params" : "data"] = body;
        if (urlParams) requestData.url = template(requestData.url, urlParams);
        if (additionalQueryParams) requestData.params = additionalQueryParams;
        if (uploadProgressReceiver) requestData.onUploadProgress = function({ loaded, total }) {
            if (total === undefined) return;
            uploadProgressReceiver(loaded / total);
        };
        if (downloadProgressReceiver) requestData.onDownloadProgress = function({ loaded, total }) {
            if (total === undefined) return;
            downloadProgressReceiver(loaded / total);
        };
        RequestManager.observerStart(requestData);
        try {
            //TODO: сделать иммутабельным
            await this.applyAllBeforeRequestMiddleware(requestData);
            const axiosResponse = await axios(requestData);
            downloadProgressReceiver?.(1);
            uploadProgressReceiver?.(1);
            await this.applyAllAfterRequestMiddleware(requestData, axiosResponse);
            RequestManager.observerSuccess(requestData, axiosResponse);
            return [
                {
                    requestData,
                    axiosResponse
                },
                null
            ];
        } catch (axiosError) {
            return [
                null,
                {
                    requestData,
                    axiosError
                }
            ];
        }
    }
    createRequest({ url, headers = {}, responseType, method, requestConfig = {}, ...otherCreateRequestOptions }) {
        // eslint-disable-next-line sonarjs/cognitive-complexity,complexity
        return async (requestData = {})=>{
            const makeRequest = (overrideRequestData = {})=>this.makeRequest({
                    url,
                    headers,
                    responseType,
                    method,
                    requestConfig,
                    requestData: mergeDeepRight(requestData, overrideRequestData)
                });
            const stack = new Error().stack;
            const makeRequestResult = await makeRequest();
            const requestError = makeRequestResult[1];
            let requestResult = makeRequestResult[0];
            if (requestError) {
                if (!requestError.axiosError.config) requestError.axiosError.config = requestError.requestData;
                requestError.axiosError.stack = stack;
                const appRequestError = AppRequestError.buildFromAxiosError(requestError.axiosError, this.requestManagerOptions.autoLogger);
                RequestManager.observerError(requestError.requestData, appRequestError);
                const convertedError = await this.applyAllErrorMiddleware(requestError.requestData, {
                    error: appRequestError,
                    requestData,
                    makeRequest
                });
                if (AppRequestError.isRequestError(convertedError)) {
                    if (requestData.onError) {
                        if (!convertedError.data.cancelled || !!requestData.runOnErrorForCanceledRequest) requestData.onError({
                            config: requestError.requestData,
                            requestError: convertedError
                        });
                    }
                    throw convertedError;
                }
                if (!isNil(convertedError)) requestResult = convertedError;
            }
            if (!requestResult) return null;
            const mutableConfig = {
                entity: otherCreateRequestOptions.entity,
                entityValidationGroups: otherCreateRequestOptions.entityValidationGroups,
                returnRaw: otherCreateRequestOptions.returnRaw ?? false,
                validateEntity: otherCreateRequestOptions.validateEntity ?? true,
                responseDataFieldPath: otherCreateRequestOptions.responseDataFieldPath ?? []
            };
            await this.appleAllAfterRequestSuccessMiddleware(mutableConfig, requestResult.axiosResponse);
            const { entity, entityValidationGroups, returnRaw, validateEntity, responseDataFieldPath } = mutableConfig;
            if (returnRaw) {
                if (requestData.onSuccess) requestData.onSuccess({
                    config: requestResult.requestData,
                    axiosResponse: requestResult.axiosResponse
                });
                return path(responseDataFieldPath, requestResult.axiosResponse.data);
            }
            if (!entity) return null;
            try {
                const plain = path(responseDataFieldPath, requestResult.axiosResponse.data);
                const instance = plainToClass(entity, "__requestSchemaTransform" in entity ? entity.__requestSchemaTransform?.(plain) : plain);
                if (validateEntity) {
                    await instance.validateAsyncAndThrowFirstError({
                        groups: entityValidationGroups
                    });
                }
                if (requestData.onSuccess) requestData.onSuccess({
                    config: requestResult.requestData,
                    axiosResponse: requestResult.axiosResponse
                });
                return instance;
            } catch (e) {
                const convertedError = await this.applyAllErrorMiddleware(requestResult.requestData, {
                    error: new AppRequestError({
                        message: e.message,
                        statusCode: -1,
                        response: requestResult.axiosResponse,
                        requestConfig: requestResult.requestData,
                        messageGroup: `Decoder error while ${method.toUpperCase()} ${url}`,
                        autoLogger: this.requestManagerOptions.autoLogger
                    }, {
                        error: e,
                        stack
                    }),
                    requestData,
                    makeRequest
                });
                if (AppRequestError.isRequestError(convertedError)) {
                    if (requestData.onError) requestData.onError({
                        config: requestResult.requestData,
                        requestError: convertedError
                    });
                    throw convertedError;
                }
                if (!isNil(convertedError)) return convertedError;
                return null;
            }
        };
    }
    constructor(baseURL = "", requestManagerOptions = {}){
        _define_property(this, "baseURL", void 0);
        _define_property(this, "requestManagerOptions", void 0);
        _define_property(this, "beforeRequestMiddleware", void 0);
        _define_property(this, "afterRequestMiddleware", void 0);
        _define_property(this, "afterRequestSuccessMiddleware", void 0);
        _define_property(this, "errorMiddleware", void 0);
        this.baseURL = baseURL;
        this.requestManagerOptions = requestManagerOptions;
        this.beforeRequestMiddleware = [];
        this.afterRequestMiddleware = [];
        this.afterRequestSuccessMiddleware = [];
        this.errorMiddleware = [];
    }
}
_define_property(RequestManager, "observers", []);

export { AppRequestError as A, METHODS as M, RequestManager as R };
//# sourceMappingURL=requestManager_chunkExport-CmN5s8gE.mjs.map
